import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Player } from '@remotion/player';
import type { PlayerRef } from '@remotion/player';
import { NewsComposition } from '../../renderer/compositions/NewsComposition';
import { PreviewControls } from './PreviewControls';
import type { CompositionProps } from '../../types';

interface PreviewPanelProps {
  compositionProps: CompositionProps | null;
  width: number;
  height: number;
  durationInFrames: number;
  fps: number;
}

export function PreviewPanel({
  compositionProps,
  width,
  height,
  durationInFrames,
  fps,
}: PreviewPanelProps) {
  const playerRef = useRef<PlayerRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);

  // ── Responsive scaling ──────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width: cw, height: ch } = entry.contentRect;
      // Fit exactly to the container (matches edit-canvas sizing, no extra breathing room)
      const scaleX = cw / width;
      const scaleY = ch / height;
      setScale(Math.min(scaleX, scaleY, 1));
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [width, height]);

  // ── Player event listeners ──────────────────────────────────
  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onFrame = ({ detail }: { detail: { frame: number } }) =>
      setCurrentFrame(detail.frame);

    p.addEventListener('play', onPlay);
    p.addEventListener('pause', onPause);
    p.addEventListener('ended', onEnded);
    p.addEventListener('frameupdate', onFrame);

    return () => {
      p.removeEventListener('play', onPlay);
      p.removeEventListener('pause', onPause);
      p.removeEventListener('ended', onEnded);
      p.removeEventListener('frameupdate', onFrame);
    };
  }, []);

  const handlePlayerKey = useCallback((e: React.KeyboardEvent) => {
    if (e.code === 'Space') {
      e.preventDefault();
      const p = playerRef.current;
      if (!p) return;
      if (isPlaying) p.pause();
      else p.play();
    }
  }, [isPlaying]);

  // ── Empty state ─────────────────────────────────────────────
  if (!compositionProps) {
    return (
      <div className="flex flex-col flex-1 bg-zinc-950">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9a2.25 2.25 0 00-2.25 2.25v9A2.25 2.25 0 004.5 18.75z" />
              </svg>
            </div>
            <p className="text-zinc-500 text-sm">Select a template to see the preview</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-zinc-950">
      {/* Preview canvas */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden"
        onKeyDown={handlePlayerKey}
        tabIndex={0}
        aria-label="Video preview — press Space to play/pause"
        style={{ outline: 'none' }}
      >
        <div
          style={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
            flexShrink: 0,
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 24px 64px rgba(0,0,0,0.6)',
          }}
        >
          <Player
            ref={playerRef}
            component={NewsComposition as unknown as React.ComponentType<Record<string, unknown>>}
            compositionWidth={width}
            compositionHeight={height}
            durationInFrames={durationInFrames}
            fps={fps}
            inputProps={compositionProps}
            style={{ width, height }}
            clickToPlay={false}
            doubleClickToFullscreen={false}
            showVolumeControls={false}
            controls={false}
          />
        </div>
      </div>

      {/* Controls bar */}
      <PreviewControls
        playerRef={playerRef}
        isPlaying={isPlaying}
        currentFrame={currentFrame}
        durationInFrames={durationInFrames}
        fps={fps}
      />
    </div>
  );
}
